import * as React from 'react';
import clip from "text-clipper";

import { getLatestAttempt } from './getLatestAttempt';
import { safeParseHtml, safeStripHtml } from './safeHtml';

import { QuestionObject, Activity } from '../interfaces';
import { PromptInterface } from '../../Staff/interfaces/evidenceInterfaces';
import { Feedback } from '../components/renderForQuestions';
import { BECAUSE, BUT, CHECKLIST, INTRODUCTION, READ_AND_HIGHLIGHT, SO } from '../utils/constants';
import { conjunctionsForActivityType } from '../../../constants/evidence';

const HIGHLIGHTED = 'highlighted'
const STEP = 'step'

export const getCurrentQuestion = ({ action, answeredQuestions, questionSet, unansweredQuestions }) => {
  const { data, question } = action;
  const dataObject = data || question;
  const key = dataObject.key ? dataObject.key : dataObject.uid;
  let currentQuestion;

  // check answeredQuestions
  // Connect has type question: { question: {...}} and Diagnostic has type question: { data: {...}}
  currentQuestion = answeredQuestions.filter((questionObject: QuestionObject) => {
    const { data, question } = questionObject;
    const dataObject = data || question || questionObject;
    // some ELL SC questions get an -esp appended to the key
    return key === dataObject.key || key === `${dataObject.key}-esp` || key === dataObject.uid;
  })[0];

  // check unansweredQuestions
  if (!currentQuestion) {
    currentQuestion = unansweredQuestions.filter((questionObject: QuestionObject) => {
      const { data, question } = questionObject;
      const dataObject = data || question || questionObject;
      return key === dataObject.key || key === `${dataObject.key}-esp` || key === dataObject.uid;;
    })[0];
  }
  // check questionSet, is title card
  if (!currentQuestion) {
    currentQuestion = questionSet.filter((questionObject: QuestionObject) => {
      const { data, question } = questionObject;
      const dataObject = data || question || questionObject;
      return key === dataObject.key || key === dataObject.uid;
    })[0];
  }
  return currentQuestion;
}

// Connect has type question: { question: {...}} and Diagnostic has type question: { data: {...}}
export const getQuestionsWithAttempts = (questions: QuestionObject[]) => {
  const questionsWithAttempts = {}

  questions.forEach(questionObject => {
    const { data, question } = questionObject;
    const dataObject = data || question || questionObject;
    const { attempts, key, uid } = dataObject;
    const keyOrUid = key ? key : uid;
    if (attempts && attempts.length) {
      questionsWithAttempts[keyOrUid] = questionObject;
    }
  });

  return questionsWithAttempts;
}

export const getFilteredQuestions = ({ questionsSlice, answeredQuestionsWithAttempts, unansweredQuestionsWithAttempts }) => {
  if (!questionsSlice.length) {
    return [];
  }
  return questionsSlice.map((questionObject: QuestionObject) => {
    const { data, question } = questionObject;
    const dataObject = data || question || questionObject;
    const key = dataObject.key ? dataObject.key : dataObject.uid;
    // some ELL SC questions get an -esp appended to the key
    const slicedKey = key.slice(0, -4);
    const answeredQuestionWithAttempts = answeredQuestionsWithAttempts[key] || answeredQuestionsWithAttempts[slicedKey];
    const unansweredQuestionWithAttempts = unansweredQuestionsWithAttempts[key] || unansweredQuestionsWithAttempts[slicedKey];

    if (answeredQuestionWithAttempts) {
      return answeredQuestionWithAttempts;
    } else if (unansweredQuestionWithAttempts) {
      return unansweredQuestionWithAttempts;
    } else {
      return questionObject;
    }
  });
}

export const getDisplayedText = ({ previewMode, question, response }) => {
  const latestAttempt = getLatestAttempt(question.attempts);

  if (previewMode && latestAttempt && latestAttempt.response && latestAttempt.response.text) {
    return latestAttempt.response.text
  }
  return response;
}

export const renderPreviewFeedback = (latestAttempt) => {
  const { response } = latestAttempt;
  const { feedback, optimal } = response;
  const strippedFeedback = safeStripHtml(feedback);

  if (optimal && strippedFeedback) {
    return <Feedback feedbackText={strippedFeedback} feedbackType="correct-matched" />
  } else if (optimal && !strippedFeedback) {
    return <Feedback feedbackText="That's a great sentence!" feedbackType="correct-matched" />
  } else if (!optimal && strippedFeedback !== '') {
    return <Feedback feedbackText={strippedFeedback} feedbackType="revise-matched" />
  }
  // we don't want to show the directions if the question has already been answered in previewMode
  return null;
}

export const returnActivity = ({ state, isGrammarActivity, isDiagnosticActivity, isConnectActivity, isEvidenceActivity }) => {
  const { grammarActivities, lessons, playDiagnostic, activities } = state;
  if (isGrammarActivity) {
    return grammarActivities.currentActivity;
  } else if (isDiagnosticActivity && playDiagnostic && playDiagnostic.diagnosticID && lessons && lessons.data) {
    const { data } = lessons;
    const { diagnosticID } = playDiagnostic;
    return data[diagnosticID]
  } else if (isConnectActivity && lessons && lessons.data) {
    const { data } = lessons;
    const uid = Object.keys(lessons.data)[0];
    return data[uid]
  } else if (isEvidenceActivity) {
    const { currentActivity } = activities;
    return currentActivity;
  }
  return null;
}

export const returnActivityData = (activityData: { data: object }) => {
  const { data } = activityData;
  if (data && Object.keys(data).length === 0) {
    return null;
  }
  return data;
}

export const returnLessonData = ({ playDiagnostic, playLesson, isConnectActivity, isDiagnosticActivity }) => {
  if (isConnectActivity) {
    return playLesson;
  } else if (isDiagnosticActivity) {
    return playDiagnostic;
  } else {
    return null;
  }
}

export const renderTitleSection = (activity: Activity) => {
  if (!activity) { return }
  return (
    <React.Fragment>
      <section className="title-section">
        <p>{safeParseHtml(activity.title || activity.name)}</p>
      </section>
      <div className="divider" />
    </React.Fragment>
  );
}

export const renderIntroductionSection = (activity: Activity, lesson: any, session: any) => {
  if (!activity) { return }
  const isEmptyIntroduction = activity.landingPageHtml && !safeStripHtml(activity.landingPageHtml);
  if (activity && (!activity.landingPageHtml || isEmptyIntroduction)) { return }

  const introductionHTML = new DOMParser().parseFromString(activity.landingPageHtml, 'text/html');
  // we strip HTML because some activites have h3 introduction text wrapped in <strong> tags
  const htmlElement = introductionHTML.getElementsByTagName('h3')[0];
  const introductionText = htmlElement && htmlElement.innerHTML ? safeStripHtml(htmlElement.innerHTML) : null;
  // in some cases, landing pages do not have h3 headers so we early return to prevent the page from crashing
  if (!introductionText) {
    return;
  }
  // highlight introduction session if activity not started, we use session for Grammar and playLesson for Connect
  const style = ((session && !session.currentQuestion) || (lesson && !lesson.currentQuestion)) ? HIGHLIGHTED : '';
  return (
    <section>
      <h2>Introduction</h2>
      <p className={`introduction-text ${style}`}>{introductionText}</p>
    </section>
  );
}

const isCurrentItem = ({ questionToPreview, uidOrKey, i, session, lesson, isDiagnosticActivity }) => {
  // some ELL SC questions get an -esp appended to the key
  const slicedUidOrKey = uidOrKey.slice(0, -4);

  let key: string | undefined;

  if (isDiagnosticActivity && lesson?.currentQuestion) {
    key = lesson.currentQuestion.data?.key;
  } else if (questionToPreview) {
    key = questionToPreview.key ?? questionToPreview.uid;
  }

  // don't set current item if activity has not started
  if ((session && !session.currentQuestion) || (lesson && !lesson.currentQuestion)) {
    return false;
  }

  // if first question has no key from initial render, is current item
  return key === uidOrKey || key === slicedUidOrKey || (i === 0 && !key);
};

const isCurrentEvidenceItem = (id: string, questionToPreview: string) => {
  return id === questionToPreview ? true : false;
}

const getIndentation = (i: number) => {
  return i < 9 ? 'indented' : '';
}

const getQuestionObject = ({ questions, titleCards, fillInBlank, key }) => {
  let questionObject;
  if (questions && questions[key] && questions[key].prompt) {
    questionObject = questions[key];
    questionObject.type = 'SC';
  } else if (titleCards && titleCards[key]) {
    questionObject = titleCards[key];
    questionObject.type = 'TL';
  } else if (fillInBlank && fillInBlank[key]) {
    questionObject = fillInBlank[key];
    questionObject.type = 'FB';
  } else {
    return {
      prompt: '',
      title: ''
    }
  }
  return questionObject;
}

export const renderQuestions = ({
  activity,
  fillInBlank,
  handleQuestionUpdate,
  lesson,
  questions,
  questionToPreview,
  session,
  titleCards,
  isDiagnosticActivity
}) => {
  if (!activity && !session) {
    return null;
    // some Grammar activities return an empty array for the questions property so we check it's length
  } else if (activity && activity.questions && activity.questions.length) {
    const questionsWithoutTitleCards = activity.questions.filter(question => question.questionType !== 'titleCards');
    return activity.questions.map((question: any, i: number) => {
      const { key } = question;
      const index = questionsWithoutTitleCards.indexOf(question);
      const questionNumber = index !== -1 ? `${index + 1}.  ` : '';
      const questionObject = getQuestionObject({ questions, titleCards, fillInBlank, key });
      const questionText = questionObject.prompt || questionObject.title
      const isCurrent = isCurrentItem({ questionToPreview, uidOrKey: key, i, session, lesson, isDiagnosticActivity });
      const highlightedStyle = isCurrent ? HIGHLIGHTED : ''
      const indentationStyle = getIndentation(i);
      const titleCardStyle = !questionNumber ? 'inverted-indent' : '';
      if (!questionObject) {
        return null;
      }
      return (
        <li key={key}>
          <button aria-current={isCurrent ? STEP : undefined} className={`question-container ${highlightedStyle} ${titleCardStyle} focus-on-light`} id={key} onClick={handleQuestionUpdate} type="button">
            {questionNumber && <p className={`question-number ${indentationStyle}`}>{questionNumber}</p>}
            <p className="question-text">{safeStripHtml(questionText)}</p>
          </button>
        </li>
      );
    })
  } else if (session && session.questionSet) {
    return session.questionSet.map((question: any, i: number) => {
      const { uid } = question;
      const isCurrent = isCurrentItem({ questionToPreview, uidOrKey: uid, i, session, lesson, isDiagnosticActivity });
      const highlightedStyle = isCurrent ? HIGHLIGHTED : ''
      const indentationStyle = getIndentation(i);
      return (
        <li key={uid}>
          <button aria-current={isCurrent ? STEP : undefined} className={`question-container ${highlightedStyle} focus-on-light`} id={uid} onClick={handleQuestionUpdate} type="button">
            <p className={`question-number ${indentationStyle}`}>{`${i + 1}.  `}</p>
            <p className="question-text">{safeStripHtml(question.prompt)}</p>
          </button>
        </li>
      );
    });
  }
}

function transformNode(node, _index) {
  if (node.type === 'tag') {
    if (node.name === 'br') { return ''; }
    if (node.name === 'strong' || node.name === 'em') { node.name = 'i'; }
    if (node.name === 'mark') { node.name = 'span' }

    if (node.children && node.children.length > 0) {
      return node.children.map((child, i) => { return transformNode(child, i) }).join('')
    }
    return node
  }
}

const renderPassageAndPrompts = ({ passage, prompts, textIsExpanded, toggleExpandedText, handleEvidenceStepUpdate, questionToPreview }) => {
  if (!passage || !prompts) { return }

  const { text } = passage;
  const clippedHtml = textIsExpanded ? text : clip(text, 350, { html: true, maxLines: 10 });
  const sanitizedHtml = (clippedHtml || '').replace(/<br\s*\/?>/gi, '');
  const buttonLabel = textIsExpanded ? 'Collapse text' : 'Preview the full text';
  const orderedPrompts = [...prompts].sort((prompt1: PromptInterface, prompt2: PromptInterface) => prompt1.conjunction.localeCompare(prompt2.conjunction));

  return (
    <React.Fragment>
      <div className="divider" />
      <section className="text-preview-section">
        <h2>Text</h2>
        {safeParseHtml(sanitizedHtml, { replace: transformNode })}
        <button className="interactive-wrapper toggle-text-button focus-on-light" onClick={toggleExpandedText} type="button">{buttonLabel}</button>
      </section>
      <div className="divider" />
      <section className="lower-prompts-preview-section">
        <h2>Prompts</h2>
        <i className="prompt-preview-instructions">Directions: Use information from the text to finish the sentence. Put the information in your own words.</i>
        <ul className="evidence-prompts">
          {orderedPrompts.map((prompt: PromptInterface) => {
            const { text, conjunction } = prompt;
            const isCurrent = isCurrentEvidenceItem(conjunction, questionToPreview)
            const highlightedStyle = isCurrent ? HIGHLIGHTED : ''
            return (
              <li key={conjunction}>
                <button aria-current={isCurrent ? STEP : undefined} className={`question-container ${highlightedStyle} focus-on-light`} id={conjunction} onClick={handleEvidenceStepUpdate} type="button">
                  <p className="question-text">{safeParseHtml(text)}...</p>
                </button>
              </li>
            )
          })}
        </ul>
      </section>
    </React.Fragment>
  )
}

const CONJUNCTION_PREVIEW_COPY = {
  [BECAUSE]: '“because” to give a reason',
  [BUT]: '“but” to give an opposing idea',
  [SO]: '“so” to give a result',
}

export const renderEvidenceActivityContent = ({ activity, toggleExpandedText, textIsExpanded, handleEvidenceStepUpdate, questionToPreview }) => {

  if (!activity) { return }

  const { prompts, passages, activity_type } = activity

  const isChecklist = isCurrentEvidenceItem(CHECKLIST, questionToPreview)
  const isIntroduction = isCurrentEvidenceItem(INTRODUCTION, questionToPreview)
  const isReadAndHighlight = isCurrentEvidenceItem(READ_AND_HIGHLIGHT, questionToPreview)

  const activeConjunctions = conjunctionsForActivityType(activity_type)
    .filter(c => prompts?.some((p: PromptInterface) => p.conjunction === c))

  return (
    <React.Fragment>
      <section className="evidence-content-section">
        <h2>What Students Will Do</h2>
        <section className="activity-steps-overview">
          <button aria-current={isChecklist ? STEP : undefined} className={`interactive-wrapper question-container ${isChecklist ? HIGHLIGHTED : ''} focus-on-light`} id={CHECKLIST} onClick={handleEvidenceStepUpdate} type="button">
            <p className="bullet-point">•</p>
            <p>See a checklist of the steps of the activity</p>
          </button>
          <button aria-current={isIntroduction ? STEP : undefined} className={`interactive-wrapper question-container ${isIntroduction ? HIGHLIGHTED : ''} focus-on-light`} id={INTRODUCTION} onClick={handleEvidenceStepUpdate} type="button">
            <p className="bullet-point">•</p>
            <p>Learn about Reading for Evidence</p>
          </button>
          <button aria-current={isReadAndHighlight ? STEP : undefined} className={`interactive-wrapper question-container ${isReadAndHighlight ? HIGHLIGHTED : ''} focus-on-light`} id={READ_AND_HIGHLIGHT} onClick={handleEvidenceStepUpdate} type="button">
            <p className="bullet-point">•</p>
            <p>Read a text and highlight sentences</p>
          </button>
          <div className="sentence-stems-explanation-container">
            <p className="bullet-point">•</p>
            <p>{`Expand ${activeConjunctions.length} sentence ${activeConjunctions.length === 1 ? 'stem' : 'stems'} about the text using...`}</p>
          </div>
        </section>
        <ul className="upper-prompts-preview-section">
          {activeConjunctions.map((conjunction, index) => {
            const isCurrent = isCurrentEvidenceItem(conjunction, questionToPreview)
            return (
              <li key={conjunction}>
                <button aria-current={isCurrent ? STEP : undefined} className={`question-container ${isCurrent ? HIGHLIGHTED : ''} focus-on-light`} id={conjunction} onClick={handleEvidenceStepUpdate} type="button">
                  <p className="question-number">{index + 1}. </p>
                  <p className="question-text">{CONJUNCTION_PREVIEW_COPY[conjunction]}</p>
                </button>
              </li>
            )
          })}
        </ul>
      </section>
      {renderPassageAndPrompts({ passage: passages[0], prompts: prompts?.filter((p: PromptInterface) => activeConjunctions.includes(p.conjunction)), textIsExpanded, toggleExpandedText, handleEvidenceStepUpdate, questionToPreview })}
    </React.Fragment>
  );
}
